import { environment } from "@env/environment";
import { BaseModel } from "./base.model";
import { OccupationSummary, Unit } from "./unit.model";
import { Building } from "./building.model";
import { Image } from "./image.model";
import { Filter } from "@interfaces/filter.interface";
import { Perspective } from "./perspective.model";
import { SUNLIGHT_SIMULATION_DEFAULT_CONFIG, SunlightSimulationConfig } from "./sunlight-simulation.model";

// Per unit-type definition of which unit properties show in the unit-details "Properties"
// section, and where: `blocks` render as the icon grid, `list` render below it as
// label -> value rows. Order within each array is the display order. See Instance.unitProps
// below - this only controls WHICH fields show, per type, and in what order; how each one is
// rendered (icon, label, value formatting) is decided in the template, per key.
export interface UnitPropsConfig {
  blocks: string[];
  list: string[];
}

export const DEFAULT_UNIT_PROPS: Record<string, UnitPropsConfig> = {
  business: {
    blocks: ['totalSquareArea', 'floor', 'fitoutStandard', 'numWorkstations'],
    list: ['address', 'subtype', 'minRentMonths'],
  },
  residential: {
    blocks: ['totalSquareArea', 'floor', 'roomNum', 'outdoorArea', 'basementArea', 'moveIn'],
    list: ['address', 'minRentMonths'],
  },
  parking: {
    blocks: ['floor', 'totalSquareArea'],
    list: ['subtype'],
  },
};

export class Poi extends BaseModel {
  id!: string;
  propertyId!: string;
  title!: string;
  teaser!: string;
  description!: string;
  distance!: string;
  distanceIcon?: string;
  perspectiveIndex?: number;
  images!: Image[];
  units!: Unit[];

  coverImageId?: number;

  coverImage?: {
    id: number;
    url: string;
  };

  getCoverImageUrl(spec: string = '') {

    if (this.coverImage?.id) {
      let url = `${environment.apiBaseUrl}/${environment.instanceUuid}/images/${this.coverImage.id}`;
      return spec ? `${url}?spec=${spec}` : url;
    }

    if (this.coverImage?.url) {
      return this.coverImage.url;
    }

    return '/assets/img/icons/gallery.svg';
  }

}

export class Info extends BaseModel {
  id!: string;
  title!: string;
  teaser!: string;
  description!: string;
  images!: any[];

  coverImageId?: number;

  coverImage?: {
    id: number;
    url: string;
  };

  getCoverImageUrl(spec: string = '') {

    if (this.coverImage?.id) {
      let url = `${environment.apiBaseUrl}/${environment.instanceUuid}/images/${this.coverImage.id}`;
      return spec ? `${url}?spec=${spec}` : url;
    }

    if (this.coverImage?.url) {
      return this.coverImage.url;
    }

    return '/assets/img/icons/gallery.svg';
  }
}

export interface Contact {
  name?: string;
  phone?: string;
  email?: string;
  imageUrl?: string;
}

export class Instance extends BaseModel {
  static UNITS_VIEW_LIST = 'list';
  static UNITS_VIEW_CARD = 'card';

  id?: number;
  uuid!: string;
  name!: string;
  config?: any;
  applicationFormConfig?: Record<string, any>;

  unitsFilter?: Filter[];

  summary!: OccupationSummary;
  perspectives!: Perspective[];
  buildings!: Building[];
  infos!: Info[];
  pois!: Poi[];
  units!: Unit[];

  coverImageId?: number;
  logoImageId?: number;

  coverImage?: { id: number; url: string; };
  logoImage?: { id: number; url: string; };
  faviconImage?: { id: number; url: string; };

  get contact(): Contact | undefined { return this.config?.contact; }
  get impressumLink(): string | null { return this.config?.impressumLink ?? null; }
  get dataSafetyLink(): string | null { return this.config?.dataSafetyLink ?? null; }
  get showLogo(): boolean { return this.config?.showLogo ?? true; }
  get hasDashboardPanel(): boolean { return this.config?.hasDashboardPanel ?? true; }
  get autoOpenUnitFilter(): boolean { return this.config?.autoOpenUnitFilter ?? false; }
  get hasLanguageSwitcher(): boolean { return this.config?.hasLanguageSwitcher ?? false; }
  get unitProps(): Record<string, UnitPropsConfig> { return this.config?.renderUnitProps ?? DEFAULT_UNIT_PROPS; }

  get defaultUnitsView(): string {
    return this.config?.defaultUnitsView === Instance.UNITS_VIEW_LIST
      ? Instance.UNITS_VIEW_LIST
      : Instance.UNITS_VIEW_CARD;
  }

 get sunlightSimulatorConfig(): SunlightSimulationConfig {
    const configured = this.config?.sunlightSimulatorConfig ?? {};

    return {
      latitude: configured.latitude ?? SUNLIGHT_SIMULATION_DEFAULT_CONFIG.latitude,
      longitude: configured.longitude ?? SUNLIGHT_SIMULATION_DEFAULT_CONFIG.longitude,
      minHour: configured.minHour ?? SUNLIGHT_SIMULATION_DEFAULT_CONFIG.minHour,
      maxHour: configured.maxHour ?? SUNLIGHT_SIMULATION_DEFAULT_CONFIG.maxHour,
      defaultHour: configured.defaultHour ?? SUNLIGHT_SIMULATION_DEFAULT_CONFIG.defaultHour,
      modelUrl: configured.modelUrl ?? SUNLIGHT_SIMULATION_DEFAULT_CONFIG.modelUrl,
      seasons: configured.seasons ?? SUNLIGHT_SIMULATION_DEFAULT_CONFIG.seasons,
    };
  }

  getUnitPropsFor(type: string): UnitPropsConfig {
    return this.unitProps[type] ?? { blocks: [], list: [] };
  }

  get childModels() {
    return {
      summary: OccupationSummary,
      perspectives: Perspective,
      buildings: Building,
      infos: Info,
      pois: Poi,
      units: Unit,
    };
  }

  getCoverImageUrl(spec: string = '') {

    if (this.coverImage?.id) {
      let url = `${environment.apiBaseUrl}/${environment.instanceUuid}/images/${this.coverImage.id}`;
      return spec ? `${url}?spec=${spec}` : url;
    }

    if (this.coverImage?.url) {
      return this.coverImage.url;
    }

    return '/assets/img/icons/gallery.svg';
  }

  getLogoImageUrl(spec: string = '') {

    if (this.logoImage?.id) {
      let url = `${environment.apiBaseUrl}/${environment.instanceUuid}/images/${this.logoImage.id}`;
      return spec ? `${url}?spec=${spec}` : url;
    }

    if (this.logoImageId) {
      let url = `${environment.apiBaseUrl}/${environment.instanceUuid}/images/${this.logoImageId}`;
      return spec ? `${url}?spec=${spec}` : url;
    }

    return '/assets/img/icons/gallery.svg';
  }

  getFaviconImageUrl(): string | null {
    if (this.faviconImage?.id) {
      return `${environment.apiBaseUrl}/${environment.instanceUuid}/images/${this.faviconImage.id}`;
    }

    return null;
  }
}
